import { FC, useCallback, useMemo, useState } from 'react';
import Location from '~/icons/location.svg';
import LocationActive from '~/icons/location-active.svg';
import CloseThinIcon from '~/icons/close-thin.svg';
import Spinner from '~/shared/components/Spinner/Spinner';
import { StoreAvailabilitySearchInput } from './StoreAvailabilitySearchInput';
import { Drawer } from '~/shared/components/Drawer';
import { ResetButton } from '~/shared/components/Button/ResetButton';
import { VirtualizedList } from '~/features/productList/components/VirtualizedList';
import { DrawerScrollContext } from '~/features/productList/components/VirtualizedList/DrawerScrollContext';
import { RefinementListRow } from '~/features/productList/components/RefinementListRow';
import RefinementEmptyList from '~/features/productList/components/FilterList/components/RefinementEmptyList';
import { useRefinementDropdownScrollLock } from '~/features/productList/components/FilterList/components';
import { FilterSelectionHeader } from '~/features/productList/components/FilterSelectionHeader';
import { useTranslation } from '~/shared/utils/translation';
import { Facets } from '~/services/instantsearch';
import { useStoreAvailability } from '~/features/productList/hooks/useStoreAvailability';
import { useStoreAvailabilityList } from '~/features/productList/hooks/useStoreAvailabilityList';
import { useStoreAvailabilitySearch } from '~/features/productList/hooks/useStoreAvailabilitySearch';
import { usePinnedRefinementItems } from '~/features/productList/hooks/usePinnedRefinementItems';
import styles from './StoreAvailabilityDrawer.module.scss';
import { useDrawerFilterTracking } from '../../hooks/useDrawerFilterTracking';
import { useScrollToTopOnSearch } from '~/features/productList/hooks/useScrollToTopOnSearch';

type StoreAvailabilityDrawerProps = {
    open: boolean;
    onClose: () => void;
};

export const StoreAvailabilityDrawer: FC<StoreAvailabilityDrawerProps> = ({ open, onClose }) => {
    const { translate } = useTranslation();
    const { trackFilter } = useDrawerFilterTracking();
    const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
    const { allStores, isLoading: isLoadingStores } = useStoreAvailabilityList({ enabled: open });

    useRefinementDropdownScrollLock(open);

    // Reopening starts from the top, so selected (pinned) stores are visible again.
    const handleClose = useCallback(() => {
        scrollElement?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        onClose();
    }, [scrollElement, onClose]);

    const {
        searchQuery,
        setSearchQuery,
        storeSearchInputRef,
        nearbyStores,
        searchError,
        toggleGeoLocation,
        isGeoActive,
        isLocating,
    } = useStoreAvailabilitySearch(allStores);

    useScrollToTopOnSearch(searchQuery, scrollElement);

    const { selectedStores, storeItems, toggleStore, clearStores } = useStoreAvailability();
    const selectedCount = selectedStores.length;

    // Cross-references the raw Salesforce store list against Algolia's facet counts.
    const items = useMemo(
        () =>
            (nearbyStores ?? []).map((store) => {
                const algoliaItem = storeItems.find((item) => item.value === store.name);

                return {
                    value: store.name,
                    label: store.name,
                    count: algoliaItem?.count ?? 0,
                    isRefined: selectedStores.includes(store.name),
                    isDisabled: !algoliaItem,
                };
            }),
        [nearbyStores, storeItems, selectedStores],
    );

    // Waits for the store-list fetch too, so the pin snapshot doesn't fire against an empty list.
    const pinnedItems = usePinnedRefinementItems(items, searchQuery, open && !isLoadingStores);

    const handleRefine = (value: string) => {
        if (!value) return;

        trackFilter(Facets.STORE, value, {
            isRefinement: false,
            isRefined: selectedStores.includes(value),
        });
        toggleStore(value);
    };

    return (
        <Drawer
            ref={setScrollElement}
            open={open}
            onClose={handleClose}
            isFilterPanel
            id="store-availability-drawer"
            ariaTitle={translate('plp.storeAvailability.selectStoreLink')}
        >
            <div className={styles.stickyHeader}>
                <div className={styles.titleRow}>
                    <span className={styles.title}>
                        {translate('plp.storeAvailability.selectStoreLink')}
                    </span>
                    <ResetButton
                        className={styles.closeButton}
                        aria-label="Close"
                        onClick={handleClose}
                    >
                        <CloseThinIcon className={styles.closeIcon} />
                    </ResetButton>
                </div>

                <div className={styles.searchRow}>
                    <StoreAvailabilitySearchInput
                        inputRef={storeSearchInputRef}
                        value={searchQuery}
                        setValue={setSearchQuery}
                        className={styles.searchInput}
                    />
                    <ResetButton
                        aria-label={translate('findStore.closeToMyLocation')}
                        aria-pressed={isGeoActive}
                        aria-busy={isLocating}
                        className={styles.locationButton}
                        disabled={isLocating}
                        onClick={toggleGeoLocation}
                    >
                        {isLocating ? <Spinner /> : isGeoActive ? <LocationActive /> : <Location />}
                    </ResetButton>
                </div>

                {searchError && <div className={styles.searchError}>{searchError}</div>}

                <FilterSelectionHeader
                    label={translate('plp.facetSelection.store')}
                    selectedCount={selectedCount}
                    onClear={clearStores}
                />
            </div>

            <DrawerScrollContext.Provider value={scrollElement}>
                {isLoadingStores ? (
                    <div className={styles.loadingContainer}>
                        <Spinner />
                    </div>
                ) : pinnedItems.length === 0 ? (
                    <RefinementEmptyList />
                ) : (
                    <VirtualizedList
                        items={pinnedItems}
                        itemKey={(item) => item.value}
                        renderItem={(item) => (
                            <RefinementListRow
                                label={item.label}
                                value={item.value}
                                count={item.count}
                                isRefined={item.isRefined}
                                isDisabled={item.isDisabled}
                                shouldShowCount
                                onRefine={handleRefine}
                            />
                        )}
                    />
                )}
            </DrawerScrollContext.Provider>
        </Drawer>
    );
};

export default StoreAvailabilityDrawer;
